import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CircleDollarSign,
  ClipboardList,
  Download,
  PackageOpen,
  Target,
  TrendingUp,
} from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import StateBadge from "@/components/ui/stateBadge";
import { OrderInvoices } from "@/components/shared/OrderInvoices";
import { StatusOrdersCompleted } from "@/components/shared/StatusOrdersCompleted";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { commercialRevenueApi } from "@/lib/api";
import {
  formatCnpj,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercentage,
} from "@/lib/formatters";
import type { CommercialRevenueOrder } from "@/types/commercialRevenue";

// Mesmo template no header e nos triggers para as colunas ficarem alinhadas.
const ORDER_COLUMNS = "md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)]";

// O endpoint devolve o status bruto de purchase_orders, não o status do portal.
function getOrderStatus(order: CommercialRevenueOrder): string {
  if (!order.contributesToRealized) return "Cancelado";
  const raw = order.status?.toLowerCase();
  if (!raw || raw === "pending" || raw === "created") return "Criado";
  return raw;
}

export default function CommercialRevenueClientDetails() {
  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const clientId = Number(clientIdParam);
  const cycleYear = Number(searchParams.get("cycleYear"));
  const validParameters = Number.isInteger(clientId)
    && clientId > 0
    && Number.isInteger(cycleYear)
    && cycleYear >= 2000
    && cycleYear <= 2100;

  const detailsQuery = useQuery({
    queryKey: ["commercial-revenue-client", clientId, cycleYear],
    queryFn: () => commercialRevenueApi.getClientDetails(clientId, cycleYear),
    enabled: validParameters,
  });

  const details = detailsQuery.data;
  const [isExporting, setIsExporting] = useState(false);

  // A API devolve um .xlsx com as abas "Pedidos", "Itens" e "Resumo".
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await commercialRevenueApi.exportClientDetailsXlsx(clientId, cycleYear);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `faturamento-cliente-${clientId}-${cycleYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: "Não foi possível exportar os pedidos do cliente",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (!validParameters) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="p-10 text-center">
            <p className="font-medium">Cliente ou ciclo inválido.</p>
            <Button variant="outline" className="mt-4" asChild>
              <Link to="/backoffice/gestao-comercial/faturamento-clientes">Voltar ao faturamento</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
          <Link to="/backoffice/gestao-comercial/faturamento-clientes">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao faturamento
          </Link>
        </Button>

        {detailsQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-96 max-w-full" />
            <Skeleton className="h-5 w-64 max-w-full" />
          </div>
        ) : details ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{details.clientName}</h1>
                <Badge variant="outline">Ciclo {details.cycleYear}</Badge>
              </div>
              <p className="mt-1 font-mono text-sm text-muted-foreground">
                CNPJ {formatCnpj(details.clientCnpj)}
              </p>
            </div>
            <Button variant="outline" onClick={handleExport} disabled={isExporting}>
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? "Exportando..." : "Exportar Excel"}
            </Button>
          </div>
        ) : null}
      </div>

      {detailsQuery.isError ? (
        <Card>
          <CardContent className="p-10 text-center">
            <p className="font-medium text-destructive">Não foi possível carregar os pedidos deste cliente.</p>
            <Button variant="outline" className="mt-4" onClick={() => detailsQuery.refetch()}>
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : detailsQuery.isLoading || !details ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-28 w-full" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Card>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="rounded-lg bg-blue-50 p-2.5 text-blue-700"><Target className="h-5 w-5" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Projeção Total do ciclo</p>
                  <p className="text-xl font-bold">{formatCurrency(details.forecastAmount)}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="rounded-lg bg-emerald-50 p-2.5 text-emerald-700"><CircleDollarSign className="h-5 w-5" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Realizado</p>
                  <p className="text-xl font-bold">{formatCurrency(details.realizedAmount)}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="rounded-lg bg-amber-50 p-2.5 text-amber-700"><TrendingUp className="h-5 w-5" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Percentual realizado</p>
                  <p className="text-xl font-bold">{formatPercentage(details.realizedPercentage)}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="rounded-lg bg-primary/10 p-2.5 text-primary"><ClipboardList className="h-5 w-5" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Pedidos do ciclo</p>
                  <p className="text-2xl font-bold">{details.orders.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <section className="space-y-3" aria-labelledby="client-cycle-goal-title">
            <h2 id="client-cycle-goal-title" className="text-lg font-semibold">Previsto 1º semestre</h2>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="rounded-lg bg-violet-50 p-2.5 text-violet-700"><Target className="h-5 w-5" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Previsto 1º semestre</p>
                    <p className="text-xl font-bold">{formatCurrency(details.goalAmount)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700"><CircleDollarSign className="h-5 w-5" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Realizado 1º semestre</p>
                    <p className="text-xl font-bold">{formatCurrency(details.goalRealizedAmount)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="rounded-lg bg-cyan-50 p-2.5 text-cyan-700"><TrendingUp className="h-5 w-5" /></div>
                  <div>
                    <p className="text-sm text-muted-foreground">Percentual realizado 1º semestre</p>
                    <p className="text-xl font-bold">{formatPercentage(details.goalRealizedPercentage)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <Card>
            <CardHeader className="border-b">
              <div>
                <CardTitle className="text-lg">Pedidos realizados</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Pedidos cancelados permanecem disponíveis para consulta, mas não compõem o valor Realizado.
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {details.orders.length === 0 ? (
                <div className="flex flex-col items-center gap-2 p-12 text-center text-muted-foreground">
                  <PackageOpen className="h-9 w-9" />
                  <p className="font-medium text-foreground">Nenhum pedido neste ciclo</p>
                  <p className="text-sm">O cliente ainda não possui pedidos para o ciclo {details.cycleYear}.</p>
                </div>
              ) : (
                <>
                  <div className="hidden items-center gap-4 border-b bg-muted/40 px-6 py-3 text-xs font-medium text-muted-foreground md:flex">
                    <div className={`grid flex-1 gap-4 pr-3 ${ORDER_COLUMNS}`}>
                      <span>Nº do pedido</span>
                      <span>Data do Pedido</span>
                      <span>Tipo</span>
                      <span>Status</span>
                      <span className="text-right">Valor</span>
                    </div>
                    <span className="w-4 shrink-0" aria-hidden />
                  </div>
                  <Accordion type="multiple" className="divide-y">
                    {details.orders.map((order) => (
                      <AccordionItem key={order.id} value={order.id} className="border-0 px-4 md:px-6">
                        <AccordionTrigger className="gap-4 py-5 text-left hover:no-underline">
                          <div className={`grid flex-1 grid-cols-2 gap-x-4 gap-y-2 pr-3 md:items-center ${ORDER_COLUMNS}`}>
                            <div className="col-span-2 md:col-span-1">
                              <div className="font-mono text-sm font-semibold">{order.orderNumber}</div>
                            </div>
                            <div className="text-sm font-normal">
                              {order.createdAt ? formatDate(new Date(order.createdAt)) : "—"}
                            </div>
                            <div>
                              <StatusOrdersCompleted orderNumber={order.orderNumber} />
                            </div>
                            <div>
                              <StateBadge status={getOrderStatus(order)} />
                              {!order.contributesToRealized && (
                                <div className="mt-1 text-xs font-normal text-destructive">Fora do Realizado</div>
                              )}
                            </div>
                            <div className="text-sm font-semibold tabular-nums md:text-right">
                              {formatCurrency(order.totalAmount)}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-5">
                          <div className="overflow-x-auto rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="min-w-[18rem]">Produto</TableHead>
                                  <TableHead className="min-w-[10rem]">Segmento</TableHead>
                                  <TableHead className="min-w-[8rem]">Série</TableHead>
                                  <TableHead className="min-w-[7rem]">Tipo</TableHead>
                                  <TableHead className="min-w-[9rem]">SKU</TableHead>
                                  <TableHead className="min-w-[9rem] text-right">Quantidade</TableHead>
                                  <TableHead className="min-w-[9rem] text-right">Valor unitário</TableHead>
                                  <TableHead className="min-w-[9rem] text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {order.items.map((item) => (
                                  <TableRow key={item.id}>
                                    <TableCell className="">{item.productDescription}</TableCell>
                                    <TableCell>{item.segmentName || "—"}</TableCell>
                                    <TableCell>{item.seriesName || "—"}</TableCell>
                                    <TableCell>
                                      <Badge
                                        variant="outline"
                                        className={item.materialUseName?.toLocaleLowerCase("pt-BR") === "professor"
                                          ? "border-violet-200 bg-violet-50 text-violet-700"
                                          : "border-blue-200 bg-blue-50 text-blue-700"}
                                      >
                                        {item.materialUseName || "—"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>{item.productSku}</TableCell>
                                    <TableCell className="text-right font-semibold tabular-nums">{formatNumber(item.realizedQuantity)}</TableCell>
                                    <TableCell className="text-right tabular-nums">{formatCurrency(item.netPrice)}</TableCell>
                                    <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(item.itemTotal)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          <OrderInvoices
                            className="mt-4 shadow-none"
                            invoices={(order.invoiceNumbers ?? []).map((invoiceNumber) => ({ invoiceNumber }))}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
